import { useEffect, useRef, useState } from "react";
import { getFormattedNumber } from "shared/utility/NumberUtility";

interface Props {
  progress: number;
  totalNumber: number;
  progressCaption?: string;
  remainingSpaceCaption?: string;
  progressColorClass?: string;
  isPaused?: boolean;
  pausedCaption?: string;
}

const ProgressBarItem = ({
  isProgress,
  coverage,
  caption,
  progressColorClass,
}: {
  isProgress: boolean;
  coverage: number;
  caption: string;
  progressColorClass?: string;
}) => {
  const getTextStyleClasses = () => {
    return "font-bold text-white flex justify-center items-center text-sm";
  };

  const widthStyle = {
    width: `${coverage}%`,
  };

  const captionRef = useRef<any>();
  const captionContainerRef = useRef<any>();
  const [isTextOverflow, setIsTextOverflow] = useState(false);

  useEffect(() => {
    if (
      captionRef.current?.offsetWidth &&
      captionContainerRef.current?.scrollWidth
    ) {
      const textSize = captionRef.current?.offsetWidth;
      const containerSize = captionContainerRef.current?.scrollWidth;
      if (textSize >= containerSize) {
        setIsTextOverflow(true);
      } else {
        setIsTextOverflow(false);
      }
    }
  }, [
    captionRef.current?.offsetWidth,
    captionContainerRef.current?.scrollWidth,
  ]);

  const getCaptionClasses = () => {
    let classes = "hidden lg:inline";
    if (isTextOverflow) {
      classes = `${classes} absolute ${isProgress ? "left-5" : "right-5"}`;
    }
    return classes;
  };

  return (
    <>
      <div
        className={`${
          isProgress
            ? progressColorClass
              ? progressColorClass
              : "bg-promDeepMint"
            : "bg-silver"
        } ${getTextStyleClasses()} ${
          coverage >= 100
            ? "rounded-lg"
            : isProgress
            ? "rounded-l-lg"
            : "rounded-r-lg"
        }`}
        style={widthStyle}
        ref={captionContainerRef}
      >
        <div className={getCaptionClasses()} title={caption} ref={captionRef}>
          {caption}
        </div>
      </div>
    </>
  );
};

export default function ProgressBar(props: Props) {
  const {
    progressCaption,
    remainingSpaceCaption,
    progressColorClass,
    isPaused,
    pausedCaption,
  } = props;

  let { progress, totalNumber } = props;

  if (!progress || progress < 0) {
    progress = 0;
  }

  if (!totalNumber || totalNumber < 0) {
    totalNumber = 0;
  }

  let progressedPercentage =
    (progress * 100) / (totalNumber > 0 ? totalNumber : 1);

  const remainingPercentage = 100 - progressedPercentage;

  const getProgressBarCaption = () => {
    return isPaused
      ? pausedCaption ?? ""
      : `${getFormattedNumber(progress)} ${progressCaption}`;
  };

  const getRemainingSpaceCaption = () => {
    if (isPaused) {
      return "";
    }
    const remaining = totalNumber - progress;
    return `${remainingSpaceCaption}: ${
      remaining < 0 ? 0 : getFormattedNumber(remaining)
    }`;
  };

  return (
    <div>
      <div className={`flex h-4 lg:h-10 rounded-lg relative`}>
        <ProgressBarItem
          isProgress={true}
          coverage={isPaused ? 100 : progressedPercentage}
          caption={getProgressBarCaption()}
          progressColorClass={progressColorClass}
        />
        <ProgressBarItem
          isProgress={false}
          coverage={isPaused ? 0 : remainingPercentage}
          caption={getRemainingSpaceCaption()}
          progressColorClass={progressColorClass}
        />
      </div>
      <div className="w-full inline lg:hidden text-sm font-medium text-promGray2">
        <div className="float-left">0</div>
        <div className="float-right">{getFormattedNumber(totalNumber)}</div>
      </div>
    </div>
  );
}

ProgressBar.defaultProps = {
  progress: 0,
  totalNumber: 0,
};
